// src/services/reactionsService.ts
import { logger } from '../utils/logger';
// Service for managing message reactions

import { supabase } from './supabase';

export interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  created_at: string;
}

export interface ReactionSummary {
  emoji: string;
  users: string[];
  count: number;
}

/**
 * Add a reaction to a message
 */
export async function addMessageReaction(messageId: string, emoji: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { success: false, error: 'Not authenticated' };
  }

  const { data, error } = await supabase
    .from('message_reactions')
    .insert({
      message_id: messageId,
      user_id: user.id,
      emoji,
    })
    .select()
    .single();

  if (error) {
    logger.error('Error adding reaction:', error);
    return { success: false, error: error.message };
  }

  return { success: true, data };
}

/**
 * Remove a reaction from a message
 */
export async function removeMessageReaction(messageId: string, emoji: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { success: false, error: 'Not authenticated' };
  }

  const { error } = await supabase
    .from('message_reactions')
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', user.id)
    .eq('emoji', emoji);

  if (error) {
    logger.error('Error removing reaction:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Get reactions for a message
 */
export async function getMessageReactions(messageId: string): Promise<{ success: boolean; data?: ReactionSummary[]; error?: string }> {
  const { data, error } = await supabase
    .from('message_reactions')
    .select('emoji, user_id')
    .eq('message_id', messageId);

  if (error) {
    logger.error('Error getting reactions:', error);
    return { success: false, error: error.message };
  }

  // Group reactions by emoji
  const reactionMap: { [emoji: string]: ReactionSummary } = {};

  data.forEach((reaction: any) => {
    if (!reactionMap[reaction.emoji]) {
      reactionMap[reaction.emoji] = {
        emoji: reaction.emoji,
        users: [],
        count: 0,
      };
    }
    reactionMap[reaction.emoji].users.push(reaction.user_id);
    reactionMap[reaction.emoji].count++;
  });

  const reactions = Object.values(reactionMap);
  return { success: true, data: reactions };
}

/**
 * Add a reaction to a group message
 */
export async function addGroupMessageReaction(messageId: string, emoji: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { success: false, error: 'Not authenticated' };
  }

  const { data, error } = await supabase
    .from('group_message_reactions')
    .insert({
      message_id: messageId,
      user_id: user.id,
      emoji,
    })
    .select()
    .single();

  if (error) {
    logger.error('Error adding group reaction:', error);
    return { success: false, error: error.message };
  }

  return { success: true, data };
}

/**
 * Remove a reaction from a group message
 */
export async function removeGroupMessageReaction(messageId: string, emoji: string) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { success: false, error: 'Not authenticated' };
  }

  const { error } = await supabase
    .from('group_message_reactions')
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', user.id)
    .eq('emoji', emoji);

  if (error) {
    logger.error('Error removing group reaction:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Get reactions for a group message
 */
export async function getGroupMessageReactions(messageId: string): Promise<{ success: boolean; data?: ReactionSummary[]; error?: string }> {
  const { data, error } = await supabase
    .from('group_message_reactions')
    .select('emoji, user_id')
    .eq('message_id', messageId);

  if (error) {
    logger.error('Error getting group reactions:', error);
    return { success: false, error: error.message };
  }

  // Group reactions by emoji
  const reactionMap: { [emoji: string]: ReactionSummary } = {};

  data.forEach((reaction: any) => {
    if (!reactionMap[reaction.emoji]) {
      reactionMap[reaction.emoji] = {
        emoji: reaction.emoji,
        users: [],
        count: 0,
      };
    }
    reactionMap[reaction.emoji].users.push(reaction.user_id);
    reactionMap[reaction.emoji].count++;
  });

  const reactions = Object.values(reactionMap);
  return { success: true, data: reactions };
}

/**
 * Subscribe to reaction changes for a message
 */
export function subscribeToReactions(messageId: string, callback: (reactions: ReactionSummary[]) => void) {
  const channel = supabase
    .channel(`reactions:${messageId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'message_reactions',
        filter: `message_id=eq.${messageId}`,
      },
      async () => {
        // Reload reactions when changed
        const result = await getMessageReactions(messageId);
        if (result.success && result.data) {
          callback(result.data);
        }
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to group message reaction changes
 */
export function subscribeToGroupReactions(messageId: string, callback: (reactions: ReactionSummary[]) => void) {
  const channel = supabase
    .channel(`group_reactions:${messageId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'group_message_reactions',
        filter: `message_id=eq.${messageId}`,
      },
      async () => {
        // Reload reactions when changed
        const result = await getGroupMessageReactions(messageId);
        if (result.success && result.data) {
          callback(result.data);
        }
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
